import { AxiosResponse } from "axios";
import { axiosInstance } from "./common";
import { ExportFormat, extractFileDownload, FileDownload } from "./download";
import { fileDateStamp } from "../lib/utils";

export enum InternalReportType {
  BORROWAL_VS_NON_BORROWAL = "BORROWAL_VS_NON_BORROWAL",
  FRAUD_BY_FIC_CATEGORY = "FRAUD_BY_FIC_CATEGORY",
}

export interface DateWindow {
  start_date?: string | null;
  end_date: string | null;
}

export type InternalReportDownloadFormat = ExportFormat.PDF | ExportFormat.DOCX;

export enum InternalReportAmountUnit {
  LAKHS = "LAKHS",
  CRORES = "CRORES",
}

export interface InternalReportDownloadRequest {
  date_windows: DateWindow[];
  format: ExportFormat;
}

const exportFileName = (
  baseName: string,
  format: ExportFormat
): string => `${baseName}_${fileDateStamp()}.${format.toLowerCase()}`;

export interface AmountUnit {
  label: string;
  divisor: number;
}

export const AMOUNT_UNIT_LAKH: AmountUnit = { label: "₹ Lakh", divisor: 100000 };
export const AMOUNT_UNIT_CRORE: AmountUnit = { label: "₹ Crore", divisor: 10000000 };

export const DEFAULT_REPORT_AMOUNT_UNIT: AmountUnit = AMOUNT_UNIT_LAKH;

export const toDisplayAmount = (
  rawRupees: number | null,
  unit: AmountUnit = DEFAULT_REPORT_AMOUNT_UNIT
): number | null => (rawRupees == null ? null : rawRupees / unit.divisor);

export interface AmountBucket {
  lt: number | null;
  gte: number | null;
}

export interface AmountBucketWithLabel extends AmountBucket {
  label: string;
}

export interface BorrowalVSNonBorrowalReportConfig {
  name: string;
  exposure_buckets: AmountBucketWithLabel[];
}

export interface BorrowalVSNonBorrowalReportData {
  num_cases_borrowal: number | null;
  total_amount_borrowal: number | null;
  num_cases_non_borrowal: number | null;
  total_amount_non_borrowal: number | null;
}

export interface BorrowalVSNonBorrowalReportDataForDateWindow {
  date_window: DateWindow;
  data: BorrowalVSNonBorrowalReportData;
}

export interface BorrowalVSNonBorrowalReportDataForAmountBucket {
  amount_bucket: AmountBucket;
  data_by_date_windows: BorrowalVSNonBorrowalReportDataForDateWindow[];
}

export interface BorrowalVSNonBorrowalReportResponse {
  config: BorrowalVSNonBorrowalReportConfig;
  data_by_amount_buckets: BorrowalVSNonBorrowalReportDataForAmountBucket[];
  totals_by_date_window: BorrowalVSNonBorrowalReportDataForDateWindow[];
}

export interface BorrowalVsNonBorrowalTableRow {
  amount_bucket_label: string | null;
  data_by_date_windows: BorrowalVSNonBorrowalReportDataForDateWindow[];
}

const BORROWAL_ENDPOINT = "/v1/internal-reports/borrowal-vs-non-borrowal";
const BORROWAL_EXPORT_BASENAME = "borrowal_vs_non_borrowal_report";

export const BorrowalVsNonBorrowalReportAPI = {
  fetch: async (
    date_windows: DateWindow[]
  ): Promise<BorrowalVSNonBorrowalReportResponse> => {
    const response: AxiosResponse<BorrowalVSNonBorrowalReportResponse> =
      await axiosInstance.post(BORROWAL_ENDPOINT, { date_windows });
    return response.data;
  },

  download: async ({
    date_windows,
    format,
  }: InternalReportDownloadRequest): Promise<FileDownload> => {
    const response = await axiosInstance.post<Blob>(
      `${BORROWAL_ENDPOINT}/export`,
      { date_windows },
      {
        params: { format, amount_unit: InternalReportAmountUnit.LAKHS },
        responseType: "blob",
      }
    );
    return extractFileDownload(
      response,
      exportFileName(BORROWAL_EXPORT_BASENAME, format)
    );
  },
};

// ---------------------------------------------------------------------------
// Fraud by FIC Category report
// ---------------------------------------------------------------------------

export interface FraudByFicCategoryReportData {
  num_cases: number | null;
  total_amount: number | null;
}

export interface FraudByFicCategoryReportDataByDateWindow {
  date_window: DateWindow;
  data: FraudByFicCategoryReportData;
}

export interface FraudByFicCategoryReportDataByCategory {
  category: string;
  data_by_date_windows: FraudByFicCategoryReportDataByDateWindow[];
}

export interface FraudByFicCategoryReportConfig {
  name: string;
}

export interface FraudByFicCategoryReportResponse {
  config: FraudByFicCategoryReportConfig;
  data_by_category: FraudByFicCategoryReportDataByCategory[];
  totals_by_date_window: FraudByFicCategoryReportDataByDateWindow[];
}

const FRAUD_BY_FIC_CATEGORY_ENDPOINT =
  "/v1/internal-reports/fraud-by-fic-category";
const FRAUD_BY_FIC_CATEGORY_EXPORT_BASENAME =
  "category_wise_fraud_summary_fiscal_report";

export const FraudByFicCategoryReportAPI = {
  fetch: async (
    date_windows: DateWindow[]
  ): Promise<FraudByFicCategoryReportResponse> => {
    const response: AxiosResponse<FraudByFicCategoryReportResponse> =
      await axiosInstance.post(FRAUD_BY_FIC_CATEGORY_ENDPOINT, { date_windows });
    return response.data;
  },

  download: async ({
    date_windows,
    format,
  }: InternalReportDownloadRequest): Promise<FileDownload> => {
    const response = await axiosInstance.post<Blob>(
      `${FRAUD_BY_FIC_CATEGORY_ENDPOINT}/export`,
      { date_windows },
      {
        params: { format, amount_unit: InternalReportAmountUnit.LAKHS },
        responseType: "blob",
      }
    );
    return extractFileDownload(
      response,
      exportFileName(FRAUD_BY_FIC_CATEGORY_EXPORT_BASENAME, format)
    );
  },
};
